const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const dataAccessMethods = require('../../../../dataAccessMethods');
const { actionUtils, config } = require('@sstdev/lib_metadata-config');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP = 'Generic button for use outside forms for more global actions';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;

    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);

    line1OfInputs
        .appendField('  Namespace:')
        .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
        .appendField('  Relation:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)), 'relation');

    // Load possible actions and default action
    const actionsDropdown = new Blockly.FieldDropdown(config.actionElementActions);
    actionsDropdown.setValue('new');
    line1OfInputs
        .appendField('  For Action:')
        .appendField(actionsDropdown, 'forAction')
        .appendField('  (optional) Subtype:')
        .appendField(new Blockly.FieldTextInput(), 'subtype')
        .appendField('  Use Grid Selection: ')
        .appendField(new Blockly.FieldCheckbox(false), 'withSelection');

    const line2OfInput = blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Button Style (old):')
        .appendField(new Blockly.FieldDropdown(config.buttonStyles), 'buttonStyle')
        .appendField('  Button Size (new):')
        .appendField(new Blockly.FieldDropdown(config.componentSize), 'size')
        .appendField('  Button Variant (new):')
        .appendField(new Blockly.FieldDropdown(config.buttonVariants), 'variant')
        .appendField('  Icon Name:')
        .appendField(new Blockly.FieldTextInput(''), 'iconName')
        .appendField('  Busy Animation Until Finished')
        .appendField(new Blockly.FieldCheckbox(), 'showBusyUntilFinished')
        .appendField('  Busy Text:')
        .appendField(new Blockly.FieldTextInput(''), 'busyText')
        .appendField('  Done Text:')
        .appendField(new Blockly.FieldTextInput(''), 'doneText');

    line2OfInput
        .appendField('  Shortcut')
        .appendField(new Blockly.FieldTextInput(), 'shortcutKey')
        .appendField('  Shift Button')
        .appendField(new Blockly.FieldCheckbox(), 'shiftButton')
        .appendField('  Control Button')
        .appendField(new Blockly.FieldCheckbox(), 'controlButton')
        .appendField('  Alt Button')
        .appendField(new Blockly.FieldCheckbox(), 'altButton');

    const line3OfInput = blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_Left)
        .appendField('  On click, auto repeat every')
        .appendField(new Blockly.FieldNumber(0, 0), 'autoRepeatSeconds')
        .appendField('Seconds');

    line3OfInput.appendField('  Hint:').appendField(new Blockly.FieldTextInput(), 'hint');
    line3OfInput
        .appendField('  Enabled by form field (optional):')
        .appendField(new Blockly.FieldTextInput(), 'enabledProperty')
        .appendField('  Title from form field (optional):')
        .appendField(new Blockly.FieldTextInput(), 'titleProperty');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation']);

    //action
    addCodeProperties(['forAction', { key: 'withSelection', handler: convertToBoolean }]);
    addCodeProperties(['subtype', 'enabledProperty', 'titleProperty']);

    //Button Display
    addCodeProperties([{ key: 'style', value: 'buttonStyle' }, 'variant', 'size', 'iconName']);
    addCodeProperties([{ key: 'showBusyUntilFinished', handler: convertToBoolean }, 'busyText', 'doneText']);

    //shortcut
    addCodeProperties([
        'shortcutKey',
        { key: 'shiftButton', handler: convertToBoolean },
        { key: 'controlButton', handler: convertToBoolean },
        { key: 'altButton', handler: convertToBoolean },
        'autoRepeatSeconds',
        'hint'
    ]);
    return code;
}

/** Builds relation metadata for the Button action and returns [subDataDictionary, partialTypeDefinition]. */
function asDataDictionary(hNode, context = {}, parentAccessRestrictions) {
    const { namespace, relation } = hNode;

    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);
    if (accessRestrictions.hidden) return [{}, {}];

    const actionTarget = actionUtils.getActionTarget(hNode);
    const normalizedAction = actionTarget.action;
    const targetNamespace = actionTarget.namespace || namespace;
    const targetRelation = actionTarget.relation || relation;

    // accessContext says what the button can do in the UI. routeAccessHints below
    // is the separate "the API route should exist because this button needs it" signal.
    const accessContext = actionUtils.buildWriteAccessContextForAction(normalizedAction, hNode.blockId);

    // Respect read-only restrictions: visible UI can show the button, but it should not
    // contribute write capabilities to route access / OpenAPI generation.
    if (accessRestrictions.readOnly) {
        accessContext.post.value = false;
        accessContext.patch.value = false;
        accessContext.remove.value = false;
    }

    // Only keep true methods as route hints. False values are not useful requests,
    // and dropping them keeps later schema merges easy to read.
    const routeAccessHints = Object.entries(accessContext).reduce((hints, [method, access]) => {
        if (access?.value) hints[method] = access;
        return hints;
    }, {});

    // Build an (extendable) relation schema; additionalProperties: true so other nodes can add props.
    const relationSchema = Type.Object(
        {},
        {
            title: targetRelation,
            // additionalProperties: true allows other blocks to extend this relation
            // This enables adding properties to the relation without breaking validation
            additionalProperties: true,
            ...(normalizedAction ? { 'x-forAction': normalizedAction } : {}),
            'x-accessRestrictions': accessRestrictions,
            'x-accessContext': accessContext,
            ...(Object.keys(routeAccessHints).length ? { 'x-routeAccessHints': routeAccessHints } : {})
        }
    );

    // subDataDictionary defines the namespace/relation structure for metadata assembly
    // This creates the hierarchical structure: namespace -> relation -> schema
    const subDataDictionary = {
        [targetNamespace]: {
            [targetRelation]: relationSchema
        }
    };

    // This button does not contribute local properties to the form
    // partialTypeDefinition remains empty as buttons are actions, not data fields
    const partialTypeDefinition = {};

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
